import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, CheckCircle2, Circle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import AgentWorkspace from '@/components/AgentWorkspace';
import IntakeWizard from '@/components/activate/IntakeWizard';
import AuditReportView from '@/components/activate/AuditReportView';
import ActionPlanView from '@/components/activate/ActionPlanView';
import OpsStatusView from '@/components/activate/OpsStatusView';
import ConnectionsSettings from '@/components/activate/ConnectionsSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- build-pipeline statuses (legacy projects) ----
const buildStatusColors = {
  idee_validatie: 'bg-blue-100 text-blue-700',
  mvp_fase: 'bg-yellow-100 text-yellow-700',
  launch_ready: 'bg-green-100 text-green-700',
};
const buildStatusLabels = {
  idee_validatie: 'Idee Validatie',
  mvp_fase: 'MVP Fase',
  launch_ready: 'Launch Ready',
};

// ---- activate-flow status stepper ----
const ACTIVATE_STEPS = [
  { key: 'draft', label: 'Koppel je app' },
  { key: 'auditing', label: 'Scan' },
  { key: 'planning', label: 'Plan opstellen' },
  { key: 'plan_ready', label: 'Plan klaar' },
  { key: 'approved', label: 'Goedgekeurd' },
  { key: 'live', label: 'Draait' },
];
const ACTIVATE_LABELS = {
  draft: 'Nieuw', auditing: 'Scannen', planning: 'Plan opstellen',
  plan_ready: 'Plan klaar', approved: 'Goedgekeurd', live: 'Draait',
};
const ACTIVATE_BADGE = {
  live: 'bg-green-100 text-green-700',
  approved: 'bg-green-100 text-green-700',
  plan_ready: 'bg-amber-100 text-amber-700',
  planning: 'bg-blue-100 text-blue-700',
  auditing: 'bg-blue-100 text-blue-700',
  draft: 'bg-muted text-muted-foreground',
};

const ACTIVATE_AGENTS = [
  { role: 'marketing_strategist', label: 'Positionering', emoji: '🎯' },
  { role: 'findability_auditor', label: 'Vindbaarheid', emoji: '🔍' },
  { role: 'inbound_marketer', label: 'Inbound', emoji: '📈' },
  { role: 'ops_director', label: 'Operatie', emoji: '⚙️' },
  { role: 'studio_integrator', label: 'Samenhang', emoji: '🧩' },
];

const BUILD_AGENTS = [
  { role: 'product_architect', label: 'Product Architect', emoji: '💡', description: 'Bedenkt 3 AI-app concepten' },
  { role: 'business_strategist', label: 'Business Strategist', emoji: '🧮', description: 'Schrijft businessplan' },
  { role: 'brand_designer', label: 'Brand Designer', emoji: '🎨', description: 'Creëert merkidentiteit' },
  { role: 'builder', label: 'Builder', emoji: '🧰', description: 'Maakt technisch plan' },
  { role: 'webflow_engineer', label: 'Webflow Engineer', emoji: '🌐', description: 'Ontwikkelt website strategie' },
  { role: 'launch_director', label: 'Launch Director', emoji: '📣', description: 'Ontwerpt launch campagne' },
  { role: 'studio_integrator', label: 'Studio Integrator', emoji: '🧩', description: 'Integreert alles' },
];

function StatusStepper({ status }) {
  const currentIdx = ACTIVATE_STEPS.findIndex((s) => s.key === status);
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {ACTIVATE_STEPS.map((step, i) => {
        const done = currentIdx >= 0 && i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step.key} className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1.5">
              {done ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                    : active ? <Loader2 className={`w-4 h-4 text-primary ${status === 'auditing' || status === 'planning' ? 'animate-spin' : ''}`} />
                    : <Circle className="w-4 h-4 text-muted-foreground" />}
              <span className={`text-xs ${active ? 'font-semibold text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
            {i < ACTIVATE_STEPS.length - 1 && <div className="w-6 h-px bg-gray-200" />}
          </div>
        );
      })}
    </div>
  );
}

function AgentOutputCard({ output }) {
  const content = output?.content
    || (output?.content_structured ? '```json\n' + JSON.stringify(output.content_structured, null, 2) + '\n```' : '');
  if (!content) {
    return <p className="text-sm text-muted-foreground">Nog geen output. Draai de activatie op het Overzicht-tabblad.</p>;
  }
  return (
    <div className="prose prose-sm max-w-none bg-muted rounded-lg p-6">
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outputs, setOutputs] = useState([]);
  const [audit, setAudit] = useState(null);
  const [tab, setTab] = useState('overview');

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`);
      setProject(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Fout bij ophalen project');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputs = async () => {
    try {
      const res = await axios.get(`${API}/agents/outputs/${projectId}`);
      setOutputs(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchAudit = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/audit`);
      setAudit(res.data);
    } catch (e) { setAudit(null); }
  };

  useEffect(() => {
    fetchProject();
    fetchOutputs();
    fetchAudit();
    // eslint-disable-next-line
  }, [projectId]);

  const isActivate = project?.kind === 'existing_app';
  const activationStatus = project?.activation_status || 'draft';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const getBuildOutput = (role) => outputs.find((o) => o.agent_role === role && o.iteration === project?.status);
  const getActivateOutput = (role) => outputs.find((o) => o.agent_role === role);

  const onIntakeComplete = () => {
    fetchProject();
    fetchOutputs();
    fetchAudit();
    setTab('plan');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background">
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Terug
            </Button>
            {isActivate && <ConnectionsSettings />}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{project?.name}</h1>
              <p className="text-muted-foreground mt-1">{project?.domain || project?.description}</p>
            </div>
            {isActivate ? (
              <Badge className={ACTIVATE_BADGE[activationStatus]} variant="secondary">
                {ACTIVATE_LABELS[activationStatus] || activationStatus}
              </Badge>
            ) : (
              <Badge className={buildStatusColors[project?.status]} variant="secondary">
                {buildStatusLabels[project?.status]}
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {isActivate ? (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-5 w-full max-w-2xl">
              <TabsTrigger value="overview" data-testid="tab-overview">Overzicht</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
              <TabsTrigger value="plan" data-testid="tab-plan">Plan</TabsTrigger>
              <TabsTrigger value="ops" data-testid="tab-ops">Operatie</TabsTrigger>
              <TabsTrigger value="agents" data-testid="tab-agents">Agents</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Voortgang</CardTitle>
                </CardHeader>
                <CardContent>
                  <StatusStepper status={activationStatus} />
                </CardContent>
              </Card>
              {(!audit || ['draft', 'auditing', 'planning'].includes(activationStatus)) ? (
                <IntakeWizard project={project} audit={audit} onComplete={onIntakeComplete} />
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Je app is gescand</CardTitle>
                    <CardDescription>
                      {activationStatus === 'plan_ready' && 'Je plan staat klaar — keur acties goed in het Plan-tabblad.'}
                      {activationStatus === 'approved' && 'Acties goedgekeurd. De operatie wordt live gezet.'}
                      {activationStatus === 'live' && 'Alles draait. Bekijk de Operatie voor de laatste runs.'}
                      {(activationStatus === 'auditing' || activationStatus === 'planning') && 'laibel is nog bezig…'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex gap-3">
                    <Button variant="outline" onClick={() => setTab('audit')}>Bekijk audit</Button>
                    <Button onClick={() => setTab('plan')}>Bekijk plan</Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="audit" className="mt-6">
              {audit ? <AuditReportView audit={audit} /> : (
                <Card className="border-dashed"><CardContent className="py-12 text-center text-muted-foreground">
                  Nog geen audit. Start de scan op het Overzicht-tabblad.
                </CardContent></Card>
              )}
            </TabsContent>

            <TabsContent value="plan" className="mt-6">
              <ActionPlanView projectId={projectId} activationStatus={activationStatus} onApproved={fetchProject} />
            </TabsContent>

            <TabsContent value="ops" className="mt-6">
              <OpsStatusView projectId={projectId} />
            </TabsContent>

            <TabsContent value="agents" className="mt-6">
              <Tabs defaultValue={ACTIVATE_AGENTS[0].role} className="w-full">
                <TabsList className="flex flex-wrap h-auto">
                  {ACTIVATE_AGENTS.map((a) => (
                    <TabsTrigger key={a.role} value={a.role} className="text-xs">
                      <span className="mr-1">{a.emoji}</span>{a.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {ACTIVATE_AGENTS.map((a) => (
                  <TabsContent key={a.role} value={a.role} className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="text-2xl">{a.emoji}</span> {a.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <AgentOutputCard output={getActivateOutput(a.role)} />
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </TabsContent>
          </Tabs>
        ) : (
          // ---- legacy build-pipeline view ----
          <Card>
            <CardHeader>
              <CardTitle>AI Agent Workspaces</CardTitle>
              <CardDescription>
                Klik op elke agent om output te genereren. Werk step-by-step door alle agents.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="product_architect" className="w-full">
                <TabsList className="grid grid-cols-7 w-full">
                  {BUILD_AGENTS.map((agent) => (
                    <TabsTrigger key={agent.role} value={agent.role} className="text-xs" data-testid={`agent-tab-${agent.role}`}>
                      <span className="mr-1">{agent.emoji}</span>{agent.label.split(' ')[0]}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {BUILD_AGENTS.map((agent) => (
                  <TabsContent key={agent.role} value={agent.role}>
                    <AgentWorkspace
                      projectId={projectId}
                      agentRole={agent.role}
                      agentLabel={agent.label}
                      agentEmoji={agent.emoji}
                      agentDescription={agent.description}
                      iteration={project?.status}
                      existingOutput={getBuildOutput(agent.role)}
                      onOutputGenerated={fetchOutputs}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
